import React, { useRef } from "react";
import { useTypedSelector } from "@hooks/useTypedSelector";
import FlexLine from "../../../components/elements/flexLine/FlexLine";
import Sticker from "../../../components/elements/sticker/Sticker";
import List from "../../../components/elements/list/List";
import Tx from "../../../types/naming";
import { ApiSelect, TextType } from "@baseTypes/baseType";
import st from "./styles.module.scss";
import { stickerLoadingWithCrop } from "../phases";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import { Empty } from "./empty";
import { WorkDashboardState } from "../../../store/reducers/dashboard/works/types";
import { identity } from "fp-ts/es6/function";
import { array } from "fp-ts";
import { IWorkDash, WorkDisplayPeriod } from "@baseTypes/service/dashboard/IWorksDashboardService";
import WorkItem from "./components/WorkItem/WorkItem";
import WorksLegend from "@pages/dashboard/works/components/WorksLegend/WorksLegend";
import urlcat from "urlcat";
import { useNavigate } from "react-router-dom";
import usePreparedStatuses from "@pages/dashboard/works/hooks/usePreparedStatuses";
import StickerHeader from "@elements/sticker/StickerHeader";

interface WorksDashboardProps {
    baseCropId?: string;
}

const selector: ApiSelect<WorkDashboardState> = createSelector(
    (state: RootState) => state.dashWorks, identity<WorkDashboardState>
)
const WorksDashboard: React.FC<WorksDashboardProps> = (props) => {

    let { data }: WorkDashboardState = useTypedSelector((state) => state.dashWorks);
    const {
        data: { byStatus },
        isApiLoading: statusesLoading
    } = useTypedSelector(state => state.vehiclesStatuses)

    const { selected } = useTypedSelector(state => state.cropSelector);
    const { selectedDate } = useTypedSelector(state => state.selectedDate);

    const listWrapperRef = useRef<HTMLDivElement | null>(null);

    const remain = Math.floor(data.reduce((acc, v) => acc + v.season.cultivatedArea, 0))
    const total = Math.floor(data.reduce((acc, v) => acc + v.season.totalArea, 0))

    const navigate = useNavigate()

    const getCommonRoutePath = (work: IWorkDash) => {
        return {
            workId: work.workId,
            date: selectedDate?.getTime(),
            planFrom: work.period.plan.from,
            planTo: work.period.plan.to,
            factFrom: work.period.fact.from,
            factTo: work.period.fact.to,
            cropRotationIds: work.cropRotationIds.join(","),
            geoZoneCultivationInfo: JSON.stringify(work.geoZoneCultivationInfo || [])
        }
    }

    const onBarClick = (period: WorkDisplayPeriod, work: IWorkDash) => navigate(urlcat(`work/detailing/:workId`, {
        period,
        ...getCommonRoutePath(work)
    }))

    const onTitleClick = ( work: IWorkDash) => {
        navigate(urlcat(`work/by-departments-and-crops`, getCommonRoutePath(work)))
    }

    const preparedStatuses = usePreparedStatuses({ statuses: byStatus, works: data })

    const renderBody = () => <>
        <FlexLine textType={TextType.Header}>
            <StickerHeader
                isLink={false}
                hintText={'В графике отображена только техника, участвующая в работах'}
                textType={TextType.Header}
                title={Tx.WorkProgress}
            />
            <WorksLegend remain={remain} total={total} />
        </FlexLine>
        <div ref={listWrapperRef} className={st.list__wrapper}>
            <List wrap={false} className={st.list}>
                {data.map((work: IWorkDash, index: number) => {
                    return <WorkItem key={work.workId + `${index}`}
                                     work={work}
                                     statusesLoading={statusesLoading}
                                     statuses={preparedStatuses[work.workId] || []}
                                     barsWidth={196}
                                     listWrapperRef={listWrapperRef}
                                     onTitleClick={() => onTitleClick(work)}
                                     onBarClick={period => onBarClick(period, work)}
                    />
                })}
            </List>
        </div>
    </>

    return (
        <Sticker className={st.sticker}>
            {data.length > 0 && selected && !array.isEmpty(selected) ? renderBody() : <Empty/>}
        </Sticker>
    );
};

export default stickerLoadingWithCrop(selector, actions => actions.fetchWorks)(WorksDashboard);
